import type { M3pStatus, PortalDashboardEnrollment } from "@dearbill/shared";
import type { BadgeTone } from "@dearbill/shared/components/Badge";

interface M3pBadge {
  label: string;
  tone: BadgeTone;
}

/**
 * Member wording for the M3P plan-enrollment status, plainer than the admin
 * labels in `frontend/admin/src/components/form/m3pLabels.ts`. A failed
 * enrollment can return to in progress, so the badge names the current status
 * only.
 */
export const M3P_STATUS_BADGE: Record<M3pStatus, M3pBadge> = {
  not_yet_started: { label: "Not started", tone: "neutral" },
  in_progress: { label: "In progress", tone: "pending" },
  submitted: { label: "Submitted", tone: "pending" },
  confirmed: { label: "Confirmed", tone: "active" },
  active: { label: "Active", tone: "active" },
  terminated: { label: "Ended", tone: "neutral" },
  failed: { label: "Not approved", tone: "error" },
};

/** Shown once the CMS-10882 form is signed while the plan enrollment has not started. */
const M3P_FORM_SIGNED_BADGE: M3pBadge = { label: "Form signed", tone: "pending" };

/**
 * The M3P program badge for an enrollment, shared by Home's programs card and
 * the My Programs M3P card. The plan-enrollment status leads once it has
 * started. Before that (no plan assigned, or `not_yet_started`), a signed
 * CMS-10882 reads as Form signed, since signing does not depend on a plan
 * assignment. A missing enrollment reads as not started.
 */
export function m3pBadge(
  enrollment: Pick<PortalDashboardEnrollment, "m3pStatus" | "esignStatus"> | null,
): M3pBadge {
  const status = enrollment?.m3pStatus ?? "not_yet_started";
  if (status === "not_yet_started" && enrollment?.esignStatus === "signed") {
    return M3P_FORM_SIGNED_BADGE;
  }
  return M3P_STATUS_BADGE[status];
}
